"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

/* ── the curved row ──────────────────────────────────────────────
 * Scroll Tilted Grid turned on its side, with the tilt drawn as a curve.
 * One row of images streams from right to left around the inside of a
 * cylinder. The centre faces you flat, the ends bend toward you so each
 * end image curves along its length, and the ends fade into the page.
 *
 * A CSS box can't bend, so every tile is cut into SLICES vertical strips,
 * each a flat facet of the cylinder showing its share of the image. The
 * strips sit still inside their tile, each turned its own few degrees
 * round the cylinder's axis, and the tile (preserve-3d) is what moves: one
 * rotateY about that same axis carries the whole tile along the arc. A
 * tile is one animation, so its strips can never drift apart or show
 * different images. Only a tile's delay differs, and that sets where on
 * the arc it sits. The band is transform-only on the compositor; the fade
 * is a static mask over it.
 *
 * The only JS is a ResizeObserver that sizes the loop, and a handler that
 * hands a tile its next image each time it wraps, out of sight.
 * ─────────────────────────────────────────────────────────────── */

// Strips per tile. Using 1 renders each card as a seamless, solid 3D card
// that follows the cylindrical arc without internal seam cracks or Z-fighting.
const SLICES = 1;

// Camera distance from the screen, as a multiple of the cylinder's radius.
// Just past the axis, so the ends come right up to the lens and swell; much
// further back and the row flattens out however tight the cylinder.
const CAMERA = 1.6;

/**
 * Cylinder radius, as a share of the hero's width, that puts the frame's
 * edge `bend` radians round the arc as seen through the camera:
 * r·sinθ · CAMERA / (CAMERA − 1 + cosθ) = width / 2, solved for r.
 */
function arc(bend: number) {
  return (CAMERA - 1 + Math.cos(bend)) / (2 * CAMERA * Math.sin(bend));
}

// A tile is never wider than this share of the hero, so a phone still
// shows its neighbours instead of one tile edge to edge.
const MAX_WIDTH = 55;

/**
 * Lays the loop on the cylinder. The radius puts the frame's edge `bend`
 * radians round the arc (see `arc`). Angles come back in degrees: `unit`
 * is the arc one tile-height spans, `limit` how far round a tile's centre
 * can go before the whole tile is off-screen, and `sweep` how far round
 * the loop's ends sit. The loop reaches past `limit`, so a tile only ever
 * wraps out of sight, and is rounded up to a whole number of tiles so the
 * gap never stretches.
 */
function measure(
  width: number,
  height: number,
  tile: number,
  aspect: number,
  gap: number,
  bend: number,
  imageCount: number = 5,
) {
  const h = Math.min(
    (tile / 100) * height,
    ((MAX_WIDTH / 100) * width) / aspect,
  );
  const radius = width * arc(bend);
  if (!(h > 0) || !(radius > 0))
    return { columns: 2, sweep: 0, unit: 0, limit: 0 };
  const deg = (rad: number) => (rad * 180) / Math.PI;
  const unit = deg(h / radius);
  const pitch = (aspect + gap / 100) * unit;
  const limit = deg(bend) + (aspect * unit) / 2;
  // Ensure enough columns to reach past limit on both sides
  const minColumns = Math.ceil((2 * limit) / pitch) + 1;
  // Round up to the nearest multiple of imageCount so the sequence forms a natural closed loop
  const validImageCount = Math.max(1, imageCount);
  const multiplier = Math.max(1, Math.ceil(minColumns / validImageCount));
  const columns = Math.min(60, multiplier * validImageCount);
  const round = (n: number) => +n.toFixed(4);
  const sweep = round((columns * pitch) / 2);
  return {
    columns,
    sweep,
    unit: round(unit),
    limit: round(Math.min(sweep, limit)),
  };
}

export type TiltedGridImage = {
  src: string;
  /** Only used if you drop the decorative treatment; the band is aria-hidden. */
  alt?: string;
  number?: string;
  title?: string;
  description?: string;
};

export type TiltedGridHeroProps = {
  /**
   * Images run along the row in order.
   */
  images: TiltedGridImage[];
  /**
   * Seconds for the row to advance by one tile. Speed is per tile, not per
   * crossing, so a phone and an ultrawide move at the same pace.
   * @default 4
   */
  speed?: number;
  /** Tile height, as a percentage of the hero's height. @default 26 */
  tileHeight?: number;
  /** Tile width divided by tile height. @default 16 / 9 */
  aspectRatio?: number;
  /** Space between tiles, as a percentage of a tile's height. @default 6 */
  gap?: number;
  /** Vertical centre of the row, as a percentage of the hero's height. @default 56 */
  axis?: number;
  /**
   * How far the row has bent round by the frame's edge, in degrees. Higher
   * curls the ends harder toward the viewer. Clamped to 5–85.
   * @default 80
   */
  curve?: number;
  /** Width of the fade at each end, as a percentage of the hero's width. @default 12 */
  fade?: number;
  /** Content rendered above the band. */
  children?: React.ReactNode;
  className?: string;
};

export function TiltedGridHero({
  images,
  speed = 4,
  tileHeight = 26,
  aspectRatio = 16 / 9,
  gap = 6,
  axis = 56,
  curve = 90,
  fade = 12,
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & TiltedGridHeroProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const id = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const orbit = `tgh-o-${id}`;
  const tile = `tgh-t-${id}`;
  const bend = (Math.min(85, Math.max(5, curve)) * Math.PI) / 180;

  // Null until the observer has measured the hero.
  const [layout, setLayout] = React.useState<ReturnType<typeof measure> | null>(
    null,
  );

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const next = measure(width, height, tileHeight, aspectRatio, gap, bend, images.length);
      setLayout((prev) =>
        prev?.columns === next.columns &&
        prev.sweep === next.sweep &&
        prev.unit === next.unit
          ? prev
          : next,
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [tileHeight, aspectRatio, gap, bend, images.length]);

  // Fetch every image up front.
  React.useEffect(() => {
    for (const { src } of images) new Image().src = src;
  }, [images]);

  const { columns, sweep, unit, limit } =
    layout ?? measure(1200, 560, tileHeight, aspectRatio, gap, bend, images.length);

  // Every length is a multiple of the tile's height, which follows the
  // hero's height until MAX_WIDTH caps it on narrow screens.
  const u = (n: number) =>
    `calc(${+n.toFixed(4)} * min(${tileHeight}cqh, ${+(
      MAX_WIDTH / aspectRatio
    ).toFixed(4)}cqw))`;

  const r = 100 * arc(bend);
  const radius = `${+r.toFixed(3)}cqw`;
  // Out to the axis, round it, back in: a box sits on the cylinder at the
  // angle rotateY gives it.
  const turn = (deg: number) =>
    `translateZ(${radius}) rotateY(${+deg.toFixed(4)}deg) translateZ(-${radius})`;

  // Off-screen transition angles:
  // Tiles wrap at +/- sweep. Near sweep (above ~120°), tiles pass behind the camera plane.
  // We keep opacity 0 while behind the camera, ramp up to opacity 1 purely in off-screen space
  // (before the tile reaches limit), and remain 100% visible throughout on-screen travel.
  // This eliminates all GPU texture drops and layer reconstruction during entry/exit!
  const safeCam = 115;
  const angleFadeStart = Math.min(safeCam, Math.max(limit + 6, (sweep + limit) / 2));
  const angleFullyVisible = limit + 2;

  const toPct = (degVal: number) =>
    Math.min(48, Math.max(0, +(((degVal + sweep) / (2 * sweep || 1)) * 100).toFixed(4)));

  const p0 = toPct(-angleFadeStart);
  const p1 = toPct(-angleFullyVisible);
  const p2 = +(100 - p1).toFixed(4);
  const p3 = +(100 - p0).toFixed(4);

  const [isHovered, setIsHovered] = React.useState(false);

  const css = React.useMemo(
    () =>
      `@keyframes ${orbit}{` +
      `from{transform:${turn(-sweep)}}to{transform:${turn(sweep)}}` +
      `0%,${p0}%{opacity:0}` +
      `${p1}%,${p2}%{opacity:1}` +
      `${p3}%,100%{opacity:0}}` +
      `@media(prefers-reduced-motion:reduce){.${tile}{animation-play-state:paused}}` +
      `#${orbit}-band:has(.${tile}:hover) .${tile}, .${tile}:hover{animation-play-state:paused !important}`,
    [orbit, sweep, p0, p1, p2, p3, radius, tile],
  );

  const share = aspectRatio / SLICES;
  const duration = columns * speed;
  const mask = `linear-gradient(90deg,transparent,#000 ${fade}%,#000 ${
    100 - fade
  }%,transparent)`;

  return (
    <div
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
      style={{ containerType: "size", ...props.style }}
    >
      <style>{css}</style>

      <div
        id={`${orbit}-band`}
        aria-hidden
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
        style={{
          opacity: layout ? 1 : 0,
          perspective: `${+(r * CAMERA).toFixed(3)}cqw`,
          perspectiveOrigin: `50% ${axis}%`,
          maskImage: mask,
          WebkitMaskImage: mask,
        }}
      >
        {layout &&
          Array.from({ length: columns }, (_, t) => {
            // Because columns is a multiple of images.length, each tile has a permanent,
            // stable image. The stream loops naturally on the GPU without any runtime re-renders!
            const imgIndex = (columns - 1 - t) % Math.max(images.length, 1);
            const img = images[imgIndex];
            return (
              <div
                key={`${layout.columns}-${t}`}
                className={cn(tile, "absolute pointer-events-auto cursor-pointer group")}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  left: `calc(50% - ${u(aspectRatio / 2)})`,
                  top: `calc(${axis}% - ${u(0.5)})`,
                  width: u(aspectRatio),
                  height: u(1),
                  transformStyle: "preserve-3d",
                  animation: `${orbit} ${duration}s linear ${-t * speed}s infinite`,
                  animationPlayState: isHovered ? "paused" : undefined,
                }}
              >
                {/* ── Single Solid 3D Card (Zero Seams, Zero Cracks) ── */}
                <div
                  className="relative w-full h-full rounded-2xl overflow-hidden select-none"
                  style={{
                    backgroundImage: img ? `url("${img.src}")` : undefined,
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  {/* Default State: Only number in blurred circle at bottom-right corner */}
                  <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 z-10 pointer-events-none transition-opacity duration-300 group-hover:opacity-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-black/40 backdrop-blur-md border border-white/20 shadow-sm">
                      <span className="text-xs sm:text-sm font-mono font-bold text-white leading-none">
                        {img?.number || `0${(imgIndex % images.length) + 1}`}
                      </span>
                    </div>
                  </div>

                  {/* On Hover State: Merges naturally into image from bottom to top with soft low opacity, ending at title */}
                  <div
                    className="absolute inset-x-0 bottom-0 h-[56%] z-20 flex flex-col justify-end px-5 sm:px-6 pb-5 sm:pb-6 pt-3 transition-all duration-300 ease-out opacity-0 translate-y-1.5 group-hover:opacity-100 group-hover:translate-y-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(0, 0, 0, 0.92) 0%, rgba(0, 0, 0, 0.72) 45%, rgba(0, 0, 0, 0.28) 80%, transparent 100%)",
                    }}
                  >
                    <h3 className="text-base sm:text-lg font-bold text-white tracking-tight leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                      {img?.number || `0${(imgIndex % images.length) + 1}`} : {img?.title || img?.alt || ""}
                    </h3>
                    {img?.description && (
                      <p className="mt-1 text-xs sm:text-sm text-white/95 leading-relaxed font-normal max-w-[290px] drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                        {img.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {children}
    </div>
  );
}

export default TiltedGridHero;
